import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Dialog, Flex, Heading, Text, useToasts } from '@applyboard/crystal-ui';
import { useNavigate } from 'react-router-dom';

import { useGradeConversionRulesEditor } from '~/hooks/useGradeConversionRulesEditor';
import { NumericDestinationRulesTable } from '../../GradeConversionDetails/components/NumericDestinationRulesTable';
import { LetterDestinationRulesTable } from '../../GradeConversionDetails/components/LetterDestinationRulesTable';
import { RulesTableActions } from '../../GradeConversionDetails/components/RulesTableActions';
import { ConversionTableMetadataForm } from '../../../components/ConversionTableMetadataForm/ConversionTableMetadataForm';
import { GradeSchemaInfo, GradePointMapping } from '~/types/gradeConversion';
import { useCreateConversionTable } from '../data/useCreateConversionTable';
import { getApiErrorMessage } from '~/utils/getApiErrorMessage';
import { ConversionTableRule } from '@ab/ams-school-api/src/services/content/v1/ConversionTables/types';
import { CountryOption, EducationLevelOption } from '../../GradeConversion/utils/referenceData';

const EMPTY_GRADE_SCHEMA: GradeSchemaInfo = {
  id: 'pending',
  name: '',
  gradeSchemaType: 'letter',
};

type MetadataState = {
  name: string;
  destinationGradeType: 'numeric' | 'letter';
  sourceInstitutionName: string;
  sourceCountryCode: string;
  gradeSchemaId: string;
  sourceEducationLevelId: string;
};

const DEFAULT_METADATA: MetadataState = {
  name: '',
  destinationGradeType: 'numeric',
  sourceInstitutionName: '',
  sourceCountryCode: '',
  gradeSchemaId: '',
  sourceEducationLevelId: '',
};

type GradeConversionCreateFormProps = {
  gradeSchemas: GradeSchemaInfo[];
  gradePointMappings: GradePointMapping[];
  educationLevels: EducationLevelOption[];
  countries: CountryOption[];
};

export function GradeConversionCreateForm({
  gradeSchemas,
  gradePointMappings,
  educationLevels,
  countries,
}: GradeConversionCreateFormProps) {
  const toast = useToasts();
  const navigate = useNavigate();

  const { createConversionTable, isCreatingConversionTable } = useCreateConversionTable();

  const [metadata, setMetadata] = useState<MetadataState>(DEFAULT_METADATA);
  const initialRules = useMemo<ConversionTableRule[]>(() => [], []);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [pendingMetadataChange, setPendingMetadataChange] = useState<{
    key: keyof MetadataState;
    value: MetadataState[keyof MetadataState];
  } | null>(null);

  const selectedGradeSchema = useMemo<GradeSchemaInfo | null>(() => {
    if (!metadata.gradeSchemaId) {
      return null;
    }
    return gradeSchemas.find((schema) => schema.id === metadata.gradeSchemaId) ?? null;
  }, [gradeSchemas, metadata.gradeSchemaId]);

  const effectiveGradeSchema = selectedGradeSchema ?? EMPTY_GRADE_SCHEMA;

  const {
    rules,
    isDirty: rulesDirty,
    addRule,
    removeRule,
    updateRule,
    resetRules,
    validateAll,
    getRulesForSubmission,
  } = useGradeConversionRulesEditor({
    initialRules,
    gradeSchema: effectiveGradeSchema,
    destinationGradeType: metadata.destinationGradeType,
    gradePointMappings,
  });

  const hasRuleChanges = useMemo(() => {
    if (rules.length > 1) {
      return true;
    }
    return rules.some((rule) => {
      return (
        rule.sourceRangeLow !== undefined ||
        rule.sourceRangeHigh !== undefined ||
        (rule.sourceLetterGrade && rule.sourceLetterGrade.trim().length > 0) ||
        rule.destinationSingleGrade !== undefined ||
        rule.destinationRangeLow !== undefined ||
        rule.destinationRangeHigh !== undefined ||
        (rule.destinationLetterGrade && rule.destinationLetterGrade.trim().length > 0)
      );
    });
  }, [rules]);

  const handleMetadataChange = <K extends keyof MetadataState>(key: K, value: MetadataState[K]) => {
    if (key === 'destinationGradeType' && value === metadata.destinationGradeType) {
      return;
    }
    if (key === 'gradeSchemaId' && value === metadata.gradeSchemaId) {
      return;
    }

    const shouldConfirmReset = (key === 'destinationGradeType' || key === 'gradeSchemaId') && hasRuleChanges;
    if (shouldConfirmReset) {
      setPendingMetadataChange({ key, value });
      setIsResetDialogOpen(true);
      return;
    }

    setMetadata((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    resetRules();
  }, [metadata.destinationGradeType, metadata.gradeSchemaId, resetRules]);

  useEffect(() => {
    if (!selectedGradeSchema) {
      return;
    }
    if (rules.length === 0) {
      addRule();
    }
  }, [rules.length, addRule, selectedGradeSchema]);

  const handleReset = () => {
    setMetadata(DEFAULT_METADATA);
    resetRules();
  };

  const metadataDirty =
    metadata.name.trim().length > 0 ||
    metadata.sourceInstitutionName.trim().length > 0 ||
    metadata.sourceCountryCode !== '' ||
    metadata.destinationGradeType !== DEFAULT_METADATA.destinationGradeType ||
    metadata.gradeSchemaId !== DEFAULT_METADATA.gradeSchemaId ||
    metadata.sourceEducationLevelId !== DEFAULT_METADATA.sourceEducationLevelId;
  const isDirty = metadataDirty || rulesDirty;

  const handleSave = async () => {
    if (!metadata.name.trim()) {
      toast.negative(new Error('Name is required.'));
      return;
    }

    if (!metadata.gradeSchemaId) {
      toast.negative(new Error('Grade schema is required.'));
      return;
    }

    if (!validateAll()) {
      toast.negative(new Error('Please fix validation issues in rules before saving.'));
      return;
    }

    try {
      const conversionTable = await createConversionTable({
        name: metadata.name.trim(),
        destinationGradeType: metadata.destinationGradeType,
        gradeSchemaId: metadata.gradeSchemaId,
        sourceInstitutionName: metadata.sourceInstitutionName.trim() || undefined,
        sourceCountryCode: metadata.sourceCountryCode || undefined,
        sourceEducationLevel: metadata.sourceEducationLevelId || undefined,
        rules: getRulesForSubmission(),
      });

      toast.positive('Grade conversion created successfully');
      navigate(`/conversion-rules/grade-conversions/${conversionTable.id}`);
    } catch (error) {
      toast.negative(new Error(getApiErrorMessage(error, 'Failed to create grade conversion.'), { cause: error }));
    }
  };

  const cancelPendingChange = () => {
    setIsResetDialogOpen(false);
    setPendingMetadataChange(null);
  };

  const confirmMetadataChange = () => {
    if (!pendingMetadataChange) {
      return;
    }
    setMetadata((prev) => ({ ...prev, [pendingMetadataChange.key]: pendingMetadataChange.value }));
    setPendingMetadataChange(null);
    setIsResetDialogOpen(false);
  };

  return (
    <Flex direction="column" gap={6}>
      <ConversionTableMetadataForm
        value={metadata}
        countries={countries}
        gradeSchemas={gradeSchemas}
        educationLevels={educationLevels}
        isGradeSchemasLoading={false}
        onChange={handleMetadataChange}
      />

      <Flex direction="column" gap={6}>
        <Heading contrast="high" level={3} variant="titleS">
          Conversion Rules
        </Heading>

        {!selectedGradeSchema ? (
          <Card emphasis="outlined">
            <Flex direction="column" justify="center" p={6}>
              <Text align="center">In order to configure grade conversion rules, you must choose a grade schema.</Text>
            </Flex>
          </Card>
        ) : metadata.destinationGradeType === 'numeric' ? (
          <NumericDestinationRulesTable
            rules={rules}
            gradeSchema={selectedGradeSchema}
            onUpdateRule={updateRule}
            onRemoveRule={removeRule}
          />
        ) : (
          <LetterDestinationRulesTable
            rules={rules}
            gradeSchema={selectedGradeSchema}
            gradePointMappings={gradePointMappings}
            onUpdateRule={updateRule}
            onRemoveRule={removeRule}
          />
        )}

        <RulesTableActions
          onAddRule={addRule}
          onSave={handleSave}
          onCancel={handleReset}
          isDirty={isDirty}
          isSaving={isCreatingConversionTable}
          isAddDisabled={!selectedGradeSchema}
          isSaveDisabled={!metadata.gradeSchemaId}
        />
      </Flex>

      <Dialog
        open={isResetDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            cancelPendingChange();
          } else {
            setIsResetDialogOpen(open);
          }
        }}
      >
        <Dialog.Heading>
          {pendingMetadataChange?.key === 'gradeSchemaId' ? 'Change grade schema?' : 'Change destination grade type?'}
        </Dialog.Heading>
        <Dialog.Content>
          <Flex direction="column" gap={2}>
            <Text>
              Changing the {pendingMetadataChange?.key === 'gradeSchemaId' ? 'grade schema' : 'destination grade type'}{' '}
              will reset all rules you have added so far. This action cannot be undone.
            </Text>
            <Text>Are you sure you want to continue?</Text>
          </Flex>
        </Dialog.Content>
        <Dialog.Actions>
          <Button intent="secondary" emphasis="transparent" onClick={cancelPendingChange}>
            Keep current type
          </Button>
          <Button intent="primary" width="fill" onClick={confirmMetadataChange}>
            Continue
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Flex>
  );
}
